// Loglar Sayfası - Admin için kullanıcı aktiviteleri
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Avatar,
  TablePagination
} from '@mui/material';
import {
  Person,
  Login,
  Logout,
  Pause,
  PlayArrow,
  Stop,
  Chat,
  Assessment,
  Refresh,
  Download,
  Visibility,
  Today,
  TrendingUp,
  People
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import dataConnectionService from '../services/dataConnectionService';
import { User } from '../types';

interface LogEntry {
  id: string;
  userId: string;
  userName: string;
  userRole: string;
  action: string;
  timestamp: Date;
  details?: string;
  type: 'login' | 'logout' | 'work_start' | 'work_end' | 'break_start' | 'break_end' | 'report_created' | 'chat_message';
}

const Logs: React.FC = () => {
  const { hasRole } = useAuth();
  
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<{start: Date | null, end: Date | null}>({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 gün önce
    end: new Date()
  });
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Mock log verilerini yükle
  const loadMockLogs = () => {
    const mockLogs: LogEntry[] = [
      {
        id: 'login_1',
        userId: '1',
        userName: 'Admin User',
        userRole: 'Admin',
        action: 'Giriş yapıldı',
        timestamp: new Date(Date.now() - 1000 * 60 * 30),
        type: 'login'
      },
      {
        id: 'login_2',
        userId: '2',
        userName: 'Developer User',
        userRole: 'Developer',
        action: 'Giriş yapıldı',
        timestamp: new Date(Date.now() - 1000 * 60 * 20),
        type: 'login'
      },
      {
        id: 'work_start_1',
        userId: '1',
        userName: 'Admin User',
        userRole: 'Admin',
        action: 'Mesai başlatıldı',
        timestamp: new Date(Date.now() - 1000 * 60 * 15),
        type: 'work_start',
        details: 'Ofis'
      },
      {
        id: 'break_start_1',
        userId: '1',
        userName: 'Admin User',
        userRole: 'Admin',
        action: 'Mola başlatıldı',
        timestamp: new Date(Date.now() - 1000 * 60 * 10),
        type: 'break_start',
        details: 'Öğle molası'
      },
      {
        id: 'break_end_1',
        userId: '1',
        userName: 'Admin User',
        userRole: 'Admin',
        action: 'Mola bitti',
        timestamp: new Date(Date.now() - 1000 * 60 * 5),
        type: 'break_end'
      },
      {
        id: 'work_end_1',
        userId: '1',
        userName: 'Admin User',
        userRole: 'Admin',
        action: 'Mesai bitti',
        timestamp: new Date(Date.now() - 1000 * 60 * 2),
        type: 'work_end'
      }
    ];
    setLogs(mockLogs);
  };

  // Log verilerini yükle
  useEffect(() => {
    loadLogs();
    loadUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtreleme
  useEffect(() => {
    let filtered = logs;

    if (filterUser !== 'all') {
      filtered = filtered.filter(log => log.userId === filterUser);
    }

    if (filterType !== 'all') {
      filtered = filtered.filter(log => log.type === filterType);
    }

    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Tarih aralığı filtresi
    if (dateRange.start) {
      filtered = filtered.filter(log => log.timestamp >= dateRange.start!);
    }
    if (dateRange.end) {
      filtered = filtered.filter(log => log.timestamp <= dateRange.end!);
    }

    setFilteredLogs(filtered);
  }, [logs, filterUser, filterType, searchTerm, dateRange]);

  // Rol kontrolü - sadece Admin erişebilir
  if (!hasRole(['admin'])) {
    return (
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        <Alert severity="error" sx={{ mt: 4 }}>
          Bu sayfaya erişim yetkiniz bulunmamaktadır.
        </Alert>
      </Container>
    );
  }

  const loadLogs = async () => {
    try {
      setLoading(true);
      
      // Data connection durumunu kontrol et
      const isDataConnected = dataConnectionService.isConnected();
      
      if (isDataConnected) {
        // Data bağlantısı varsa gerçek log verilerini yükle
        const logsResult = await dataConnectionService.getLogs();
        const allUsers = await dataConnectionService.getUsers();
        
        if (logsResult.success && logsResult.data) {
          // Gerçek log verilerini kullan
          const realLogs: LogEntry[] = logsResult.data.map((log: any) => {
            const user = allUsers.find(u => u.id === log.userId);
            return {
              id: log.id,
              userId: log.userId,
              userName: user?.username || 'Bilinmeyen Kullanıcı',
              userRole: user?.role || 'Bilinmeyen',
              action: log.action,
              timestamp: new Date(log.timestamp),
              type: log.action as any,
              details: log.details?.notes || log.details?.location || undefined
            };
          });
          
          setLogs(realLogs);
        } else {
          // Log verileri alınamadı, mock verileri kullan
          console.warn('Log verileri alınamadı, mock veriler kullanılıyor:', logsResult.message);
          loadMockLogs();
        }
      } else {
        // Data bağlantısı yoksa mock verileri göster
        loadMockLogs();
      }
    } catch (error) {
      console.error('Log yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };


  const loadUsers = async () => {
    try {
      const userData = await dataConnectionService.getUsers();
      setUsers(userData);
    } catch (error) {
      console.error('Kullanıcı yükleme hatası:', error);
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'login': return <Login color="success" />;
      case 'logout': return <Logout color="error" />;
      case 'work_start': return <PlayArrow color="primary" />;
      case 'work_end': return <Stop color="error" />;
      case 'break_start': return <Pause color="warning" />;
      case 'break_end': return <PlayArrow color="success" />;
      case 'report_created': return <Assessment color="info" />;
      case 'chat_message': return <Chat color="secondary" />;
      default: return <Person />;
    }
  };

  const getActionColor = (type: string) => {
    switch (type) {
      case 'login': return 'success';
      case 'logout': return 'error';
      case 'work_start': return 'primary';
      case 'work_end': return 'error';
      case 'break_start': return 'warning';
      case 'break_end': return 'success';
      case 'report_created': return 'info';
      case 'chat_message': return 'secondary';
      default: return 'default';
    }
  };

  const formatTimestamp = (timestamp: Date) => {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Şimdi';
    if (minutes < 60) return `${minutes} dk önce`;
    if (hours < 24) return `${hours} saat önce`;
    return `${days} gün önce`;
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Admin': return 'error';
      case 'Developer': return 'secondary';
      case 'Call Admin': return 'primary';
      case 'LiveChat Admin': return 'success';
      case 'Risk Admin': return 'warning';
      default: return 'default';
    }
  };

  // İstatistikler
  const getLogStats = () => {
    const totalLogs = filteredLogs.length;
    const todayLogs = filteredLogs.filter(log => 
      log.timestamp.toDateString() === new Date().toDateString()
    ).length;
    
    const typeStats = filteredLogs.reduce((acc, log) => {
      acc[log.type] = (acc[log.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const userStats = filteredLogs.reduce((acc, log) => {
      acc[log.userName] = (acc[log.userName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return { totalLogs, todayLogs, typeStats, userStats };
  };

  const stats = getLogStats();

  // Sayfalama için logları dilimle
  const paginatedLogs = filteredLogs.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Sistem Logları
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Kullanıcı aktiviteleri ve sistem olayları
        </Typography>
        
        {/* Data Connection Durumu */}
        {!dataConnectionService.isConnected() && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Mock Veri Modu:</strong> Data bağlantısı kurulmadığı için örnek log verileri gösteriliyor. 
              Gerçek veriler için Sayfa Ayarları'ndan data bağlantısı kurun.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* İstatistik Kartları */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
        gap: 3, 
        mb: 4 
      }}>
        <Card sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.totalLogs}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Toplam Log
              </Typography>
            </Box>
            <Assessment sx={{ fontSize: 40, opacity: 0.8 }} />
          </Box>
        </Card>

        <Card sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {stats.todayLogs}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Bugünkü Loglar
              </Typography>
            </Box>
            <Today sx={{ fontSize: 40, opacity: 0.8 }} />
          </Box>
        </Card>

        <Card sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {Object.keys(stats.typeStats).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Aktivite Türü
              </Typography>
            </Box>
            <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
          </Box>
        </Card>

        <Card sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {Object.keys(stats.userStats).length}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Aktif Kullanıcı
              </Typography>
            </Box>
            <People sx={{ fontSize: 40, opacity: 0.8 }} />
          </Box>
        </Card>
      </Box>

      {/* Filtreler */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(6, 1fr)' }, 
            gap: 3 
          }}>
            <Box>
              <TextField
                label="Ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                fullWidth
                placeholder="Kullanıcı, aktivite veya detay ara..."
              />
            </Box>
            
            <Box>
              <FormControl size="small" fullWidth>
                <InputLabel>Kullanıcı</InputLabel>
                <Select
                  value={filterUser}
                  onChange={(e) => setFilterUser(e.target.value)}
                  label="Kullanıcı"
                >
                  <MenuItem value="all">Tüm Kullanıcılar</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <FormControl size="small" fullWidth>
                <InputLabel>Aktivite</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Aktivite"
                >
                  <MenuItem value="all">Tüm Aktiviteler</MenuItem>
                  <MenuItem value="login">Giriş</MenuItem>
                  <MenuItem value="logout">Çıkış</MenuItem>
                  <MenuItem value="work_start">Mesai Başlat</MenuItem>
                  <MenuItem value="work_end">Mesai Bitir</MenuItem>
                  <MenuItem value="break_start">Mola Başlat</MenuItem>
                  <MenuItem value="break_end">Mola Bitir</MenuItem>
                  <MenuItem value="report_created">Rapor Oluştur</MenuItem>
                  <MenuItem value="chat_message">Chat Mesajı</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box>
              <TextField
                label="Başlangıç Tarihi"
                type="date"
                value={dateRange.start ? dateRange.start.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value ? new Date(e.target.value) : null }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box>
              <TextField
                label="Bitiş Tarihi"
                type="date"
                value={dateRange.end ? dateRange.end.toISOString().split('T')[0] : ''}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value ? new Date(e.target.value) : null }))}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
              <Tooltip title="Yenile">
                <IconButton onClick={loadLogs} disabled={loading}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Dışa Aktar">
                <IconButton>
                  <Download />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Log Tablosu */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kullanıcı</TableCell>
                  <TableCell>Aktivite</TableCell>
                  <TableCell>Detay</TableCell>
                  <TableCell>Zaman</TableCell>
                  <TableCell>IP Adresi</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                          {log.userName.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {log.userName}
                          </Typography>
                          <Chip 
                            label={log.userRole} 
                            size="small" 
                            color={getRoleColor(log.userRole) as any}
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getActionIcon(log.type)}
                        <Typography variant="body2">
                          {log.action}
                        </Typography>
                        <Chip 
                          label={log.type.replace('_', ' ')} 
                          size="small" 
                          color={getActionColor(log.type) as any}
                          variant="filled"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {log.details || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatTimestamp(log.timestamp)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.timestamp.toLocaleString('tr-TR')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {log.type === 'login' ? '192.168.1.100' : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Detayları Görüntüle">
                        <IconButton size="small">
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Sayfalama */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredLogs.length}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Sayfa başına satır:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} / ${count !== -1 ? count : `${to} ve daha fazlası`}`
            }
          />
        </CardContent>
      </Card>

      {filteredLogs.length === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Filtrelere uygun log bulunamadı
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default Logs;
